/**
 * Alpine handler for the moderator appeal detail page.
 * Posts approve/deny decisions to /v1/appeals/{id}/decide via window.ApiClient.
 *
 * Wires up the data shape consumed by mod/appeals/detail.twig:
 *   - activeDecision
 *   - approve.{reasonCategory, logMsg}
 *   - deny.{denialOutcomeCategory, logMsg, subtype, cooldownDays}
 *
 * Confirmation goes through window.ConfirmModal so the action is acknowledged
 * before the request is sent. A 409 response is treated as "already decided"
 * and surfaces a friendly message rather than a generic error.
 */

function postFormUrlEncoded(endpoint, params) {
    const body = new URLSearchParams();
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === null || value === undefined || value === '') {
            return;
        }
        body.append(key, String(value));
    });

    return window.ApiClient.request(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
    });
}

function confirmDecision(options) {
    return new Promise((resolve) => {
        if (!window.ConfirmModal || typeof window.ConfirmModal.show !== 'function') {
            resolve(window.confirm(options.message));
            return;
        }
        window.ConfirmModal.show({
            title: options.title,
            message: options.message,
            confirmText: options.confirmText,
            cancelText: 'Cancel',
            type: options.type || 'warning',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

export function modAppealDecide(config = {}) {
    return {
        appealId: config.appealId,
        // appealsListUrl is always passed from detail.twig (via LinksHelper).
        // Keep an empty default so a missing config surfaces as a no-op
        // redirect rather than a hardcoded path that could drift.
        appealsListUrl: config.appealsListUrl || '',
        // Pointer to the next open appeal in queue order, when one exists.
        // After a decision lands, redirect there so moderators chain through
        // the queue without bouncing back to the index. Empty when this was
        // the last open appeal — the action falls back to the queue list.
        nextAppealUrl: config.nextAppealUrl || '',
        activeDecision: '',
        error: '',

        approve: {
            reasonCategory: 'restore-mistake',
            logMsg: '',
        },
        deny: {
            denialOutcomeCategory: '',
            logMsg: '',
            subtype: '',
            cooldownDays: '',
        },

        selectDecision(decision) {
            this.activeDecision = decision;
            this.error = '';
        },

        async approveAppeal() {
            this.error = '';
            if (this.activeDecision !== 'approved') {
                this.activeDecision = 'approved';
                return;
            }

            try {
                const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                    decision: 'approved',
                    reason_category: this.approve.reasonCategory,
                    log_msg: this.approve.logMsg,
                });

                if (response.ok) {
                    window.location.href = this.nextAppealUrl || this.appealsListUrl;
                    return;
                }
                await this.handleErrorResponse(response);
            } catch (err) {
                this.error = (err && err.message) ? err.message : 'Network error';
            }
        },

        async denyAppeal() {
            this.error = '';
            if (this.activeDecision !== 'denied') {
                this.activeDecision = 'denied';
                return;
            }
            if (!this.deny.denialOutcomeCategory) {
                this.error = 'Outcome category is required.';
                return;
            }
            const cooldownInput = String(this.deny.cooldownDays).trim();
            const cooldown = cooldownInput === '' ? null : Number(cooldownInput);
            if (
                cooldown !== null
                && (!/^\d+$/.test(cooldownInput) || !Number.isSafeInteger(cooldown) || cooldown < 1 || cooldown > 365)
            ) {
                this.error = 'Cooldown must be a whole number from 1-365 days.';
                return;
            }

            // Long cooldowns (>= 90 days) push the user into a state that's
            // hard to recover from in a hurry, so escalate the modal to
            // "danger" and call out the cooldown length.
            const longCooldown = cooldown !== null && cooldown >= 90;
            const ok = await confirmDecision({
                title: 'Deny appeal',
                message: longCooldown
                    ? `Deny this appeal with a ${cooldown}-day cooldown? The user cannot resubmit until that date.`
                    : 'Deny this appeal? The user will see the configured outcome on their status page.',
                confirmText: 'Deny appeal',
                type: longCooldown ? 'danger' : 'warning',
            });
            if (!ok) {
                return;
            }

            try {
                const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                    decision: 'denied',
                    denial_outcome_category: this.deny.denialOutcomeCategory,
                    log_msg: this.deny.logMsg,
                    subtype: this.deny.subtype,
                    cooldown_days: cooldown,
                });

                if (response.ok) {
                    window.location.href = this.nextAppealUrl || this.appealsListUrl;
                    return;
                }
                await this.handleErrorResponse(response);
            } catch (err) {
                this.error = (err && err.message) ? err.message : 'Network error';
            }
        },

        async handleErrorResponse(response) {
            if (response.status === 409) {
                this.error = 'This appeal has already been decided. Refresh to see the latest state.';
                return;
            }
            let message = 'Request failed (' + response.status + ').';
            try {
                const body = await response.json();
                if (body && body.error && body.error.message) {
                    message = body.error.message;
                }
            } catch (_e) {
                // Body wasn't JSON; fall back to generic message.
            }
            this.error = message;
        },
    };
}

/**
 * Queue row click-through. Rows on the appeals index page are non-anchor
 * containers tagged with .js-appeal-row + data-appeal-url. Clicking anywhere
 * outside an inner <a>/<button> navigates to the detail page; modifier keys
 * and middle-click open a new tab; Enter/Space activate when focused.
 */
function initAppealQueueRows(scope) {
    const root = scope || document;

    root.addEventListener('click', (e) => {
        if (e.target.closest('a, button')) {
            return;
        }
        const row = e.target.closest('.js-appeal-row');
        if (!row || !row.dataset.appealUrl) {
            return;
        }
        if (e.metaKey || e.ctrlKey || e.button === 1) {
            window.open(row.dataset.appealUrl, '_blank', 'noopener');
        } else {
            window.location.href = row.dataset.appealUrl;
        }
    });

    root.addEventListener('auxclick', (e) => {
        if (e.button !== 1 || e.target.closest('a, button')) {
            return;
        }
        const row = e.target.closest('.js-appeal-row');
        if (!row || !row.dataset.appealUrl) {
            return;
        }
        e.preventDefault();
        window.open(row.dataset.appealUrl, '_blank', 'noopener');
    });

    root.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') {
            return;
        }
        const active = document.activeElement;
        if (!active || !active.classList.contains('js-appeal-row') || !active.dataset.appealUrl) {
            return;
        }
        e.preventDefault();
        window.location.href = active.dataset.appealUrl;
    });
}

/**
 * Investigation panels loader on the appeal detail page. Each panel host
 * (.js-investigation-panel) carries data-panel-url. Loaded HTML is injected
 * via innerHTML and Alpine/Lucide are re-initialized.
 */
function initAppealInvestigationPanels() {
    const root = document.getElementById('appeal-investigation-panels');
    if (!root) {
        return;
    }

    const fetchOptions = {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    };

    root.querySelectorAll('.js-investigation-panel').forEach((host) => {
        const url = host.dataset.panelUrl;
        const label = host.dataset.panelLabel || 'panel';
        if (!url) {
            return;
        }

        fetch(url, fetchOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Panel request failed');
                }
                return response.text();
            })
            .then((html) => {
                host.innerHTML = html;
                window.Alpine?.initTree(host);
                window.LucideIcons?.initialize(host);
            })
            .catch(() => {
                const errorBox = document.createElement('div');
                errorBox.className = 'bg-white rounded-lg border border-red-200 px-4 py-4 text-sm text-red-600';
                errorBox.textContent = label + ' could not load.';
                while (host.firstChild) {
                    host.removeChild(host.firstChild);
                }
                host.appendChild(errorBox);
            });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initAppealQueueRows();
        initAppealInvestigationPanels();
    });
} else {
    initAppealQueueRows();
    initAppealInvestigationPanels();
}
